// A look's shape, shared by the page (looks.js draws it, dress.js edits it) and the office (src/office/look.ts and
// src/otterbonk/run.ts validate with cleanLook; the worker bundles this file, so it stays pure: no imports, no DOM).
//
// A look on the wire / in storage:
//   { fur, belly, scarf, body, acc, tint? }
//   fur, belly, scarf: palette indices (looks.js FURS / BELLIES / SCARVES; scarf's last entry is 「无」)
//   body:              体型, looks.js BODIES
//   acc:               the accessories worn, ids from ACCESSORIES
//   tint:              custom colours {fur?, belly?, scarf?: '#rrggbb'} over the palette pick (optional); the page
//                      turns each into a shade ramp (looks.js), so a custom colour recolours like a palette one.

/** 体型: the 3D bodies in look.body order (looks.js BODIES; assets/otters3d BODIES keys; the office accepts
 *  body 0 .. BODY_KEYS.length - 1). The cats: 5 K 猫 (Deck's own), 6 M 月薪喵 (the sticker cat), 7 G 灰头猫 (the first
 *  月薪喵). To drop one (the last, so no other body's number moves), take its key out of here and run `assets/otters3d/cats.sh --manifest` (rewrites otter3d/geo.js and
 *  otterbonk/gear.js from what's listed here; nothing is re-rendered), then delete its PNGs. The rest is derived. */
export const BODY_KEYS = ['0', 'A', 'B', 'C', 'Y', 'K', 'M', 'G'];
/** The cats among them (装扮 shows them in a row of their own; 双人互动 has cat versions). */
export const CAT_KEYS = ['K', 'M', 'G'];
/** Is a look's body one of the cats? */
export const isCatLook = (l) => Number.isInteger(l?.body) && CAT_KEYS.includes(BODY_KEYS[l.body]);
/** Bodies behind a feature flag (src/flags.ts, on per Raft server): body key -> [flag, the body shown instead where
 *  it's off]. 月薪喵 is on only for some servers (src/flags.ts); elsewhere it shows as 猫. */
export const BODY_FLAGS = { M: ['body-M', 'K'] };
/** May a look wear body b where `flags` (the office's, src/flags.ts flagsFor) are on? */
export const bodyOn = (b, flags) => { const f = BODY_FLAGS[BODY_KEYS[b]]; return !f || (flags ?? []).includes(f[0]); };
/** The look as shown where `flags` are on: a body that's off there becomes its stand-in (the saved look is kept). */
export function flaggedLook(l, flags) {
  if (!l || !Number.isInteger(l.body) || bodyOn(l.body, flags)) return l;
  return { ...l, body: BODY_KEYS.indexOf(BODY_FLAGS[BODY_KEYS[l.body]][1]) };
}

/** Every accessory: its name, slot and 3D overlay layer, in drawing order (later ones on top). */
export const ACCESSORIES = [
  { id: 'bowtie', name: 'dress.acc.bowtie', slot: 'neck' },
  { id: 'phones', name: 'dress.acc.phones', slot: 'other' },
  { id: 'glasses', name: 'dress.acc.glasses', slot: 'face' },
  { id: 'shades', name: 'dress.acc.shades', slot: 'face' },
  { id: 'beanie', name: 'dress.acc.beanie', slot: 'hat' },
  { id: 'straw', name: 'dress.acc.straw', slot: 'hat' },
  { id: 'captain', name: 'dress.acc.captain', slot: 'hat' },
  { id: 'flower', name: 'dress.acc.flower', slot: 'other' },
  { id: 'swimring', name: 'dress.acc.swimring', slot: 'other' },
];
/** Slots as the dress-up panel groups them (name: a catalog key). `one`: at most one accessory from the slot (two hats don't stack). */
export const SLOTS = [
  { id: 'hat', name: 'dress.slot.hat', one: true },
  { id: 'face', name: 'dress.slot.face', one: true },
  { id: 'neck', name: 'dress.slot.neck', one: false },
  { id: 'other', name: 'dress.slot.other', one: false },
];
/** Pairs from different slots that can't be worn together (they'd draw over each other): the flower sits where a
 *  wide brim does. Everything else combines, e.g. 耳机 + 领结 + 小花. */
export const CLASHES = [['flower', 'straw'], ['flower', 'captain']];

const BY_ID = new Map(ACCESSORIES.map((a, i) => [a.id, { ...a, i }]));
export const accById = (id) => BY_ID.get(id);
/** Do accessories a and b (ids) collide? */
export function clash(a, b) {
  if (a === b) return false;
  const A = BY_ID.get(a), B = BY_ID.get(b);
  if (!A || !B) return true;
  if (A.slot === B.slot && SLOTS.find((s) => s.id === A.slot)?.one) return true;
  return CLASHES.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
}
/** acc after putting on `id`: whatever it collides with comes off. */
export const wear = (acc, id) => [...acc.filter((x) => !clash(x, id) && x !== id), id].sort((x, y) => BY_ID.get(x).i - BY_ID.get(y).i);
/** A valid accessory list (known ids, no repeats, nothing colliding), in drawing order; null if it isn't one. */
export function okAcc(acc) {
  if (!Array.isArray(acc) || acc.length > ACCESSORIES.length) return null;
  if (!acc.every((id) => typeof id === 'string' && BY_ID.has(id))) return null;
  if (new Set(acc).size !== acc.length) return null;
  for (let i = 0; i < acc.length; i++) for (let j = i + 1; j < acc.length; j++) if (clash(acc[i], acc[j])) return null;
  return [...acc].sort((x, y) => BY_ID.get(x).i - BY_ID.get(y).i);
}
/** A valid accessory list for drawing; malformed input draws no accessories. */
export const accOf = (l) => okAcc(l?.acc) ?? [];
const HEX = /^#[0-9a-f]{6}$/;
/** A valid tint ({fur?, belly?, scarf?}: '#rrggbb'), or null when there's none. */
export function okTint(t) {
  if (!t || typeof t !== 'object' || Array.isArray(t)) return null;
  const o = {};
  for (const k of ['fur', 'belly', 'scarf']) if (typeof t[k] === 'string' && HEX.test(t[k])) o[k] = t[k];
  return Object.keys(o).length ? o : null;
}
const PARTS = ['fur', 'belly', 'scarf'];
/** How many choices each palette part has (public/looks.js FURS, BELLIES, SCARVES: looks.js checks
 *  they agree), so the office can make the same default look as the page (seedParts) without the art. */
export const PART_COUNTS = { fur: 6, belly: 3, scarf: 8 };
function hash(s) { let h = 2166136261; for (const ch of s) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); } return h >>> 0; }
/** The look made from an id (before anyone dresses up), as palette picks and a body: looks.js seedLook draws it; the
 *  office sends it to guests instead of the id it comes from (office/guests.ts guestView). */
export function seedParts(seed) {
  let h = hash(String(seed));
  const pick = (n) => { const v = h % n; h = Math.imul(h ^ (h >>> 15), 2246822519) >>> 0; return v; };
  const C = PART_COUNTS;
  const fur = pick(C.fur), belly = pick(C.belly), scarf = pick(C.scarf - 1);
  const hats = ['beanie', 'straw', 'captain', 'flower'], faces = ['glasses', 'shades'], extras = ['phones', 'bowtie'];
  const hat = pick(3) === 0 ? hats[pick(hats.length)] : null;
  const face = pick(4) === 0 ? faces[pick(faces.length)] : null;
  const extra = pick(5) === 0 ? extras[pick(extras.length)] : null;
  const body = 1 + pick(3);
  return { fur, belly, scarf, body, acc: okAcc([hat, face, extra].filter(Boolean)) };
}
/** A look as the office keeps it, or null if it is invalid. `tint` keeps only valid colours. */
export function cleanLook(l, bodyCount) {
  if (!l || typeof l !== 'object' || !PARTS.every((k) => Number.isInteger(l[k]) && l[k] >= 0 && l[k] < 32)) return null;
  if (!(Number.isInteger(l.body) && l.body >= 0 && l.body < bodyCount)) return null;
  const acc = okAcc(l.acc); if (!acc) return null;
  if (l.tint !== undefined && l.tint !== null && !okTint(l.tint)) return null;
  const o = { fur: l.fur, belly: l.belly, scarf: l.scarf, body: l.body, acc };
  const tint = okTint(l.tint);
  if (tint) o.tint = tint;
  return o;
}
